import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, TrendingDown, MousePointerClick, Tag, Award } from 'lucide-react';
import type { AffiliateClick, CategoryClick } from '@/hooks/affiliate/useAffiliateAnalytics';

interface AffiliateAnalyticsOverviewProps {
  totalClicks: number;
  previousTotalClicks: number;
  clicksGrowth: number;
  topCategory: CategoryClick | null;
  topPartner: AffiliateClick | null;
}

export const AffiliateAnalyticsOverview = ({ 
  totalClicks, 
  previousTotalClicks,
  clicksGrowth,
  topCategory, 
  topPartner 
}: AffiliateAnalyticsOverviewProps) => {
  const isGrowthPositive = clicksGrowth >= 0;
  
  return (
    <div className="grid gap-4 md:grid-cols-3 mb-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Affiliate Clicks</CardTitle>
          <MousePointerClick className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{totalClicks.toLocaleString()}</div>
          <div className="flex items-center text-xs text-muted-foreground mt-1">
            {isGrowthPositive ? (
              <TrendingUp className="mr-1 h-3 w-3 text-green-500" />
            ) : (
              <TrendingDown className="mr-1 h-3 w-3 text-red-500" />
            )}
            <span className={isGrowthPositive ? "text-green-500" : "text-red-500"}>
              {Math.abs(clicksGrowth).toFixed(1)}%
            </span>
            <span className="ml-1">from previous period</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Previous: {previousTotalClicks.toLocaleString()}
          </p>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Top Category</CardTitle>
          <Tag className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {topCategory ? topCategory.category : 'N/A'}
          </div>
          <p className="text-xs text-muted-foreground">
            {topCategory ? `${topCategory.clicks.toLocaleString()} clicks` : 'No data'}
          </p>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Top Performing Partner</CardTitle>
          <Award className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {topPartner ? topPartner.partner_name : 'N/A'}
          </div>
          <p className="text-xs text-muted-foreground">
            {topPartner ? `${topPartner.clicks.toLocaleString()} clicks` : 'No data'}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
